// A mensagem de falha ao baixar o CCB, nas duas rotas que o servem.
//
// POR QUE UM MÓDULO, e não dois blocos de `if` dentro das telas: as duas
// páginas (servidor e prefeitura) traduzem o MESMO conjunto de reasons, e cada
// uma tinha a sua lista — com textos divergentes, um `r2_indisponivel` tratado
// só de um lado, e dois fallbacks que imprimiam o reason cru na tela. Aqui a
// tradução é uma só, e testável sem montar página nenhuma.
//
// DUAS MUDANÇAS DE COMPORTAMENTO ESTÃO CODIFICADAS AQUI:
//
// 1. `contrato_nao_e_seu` e `contrato_nao_e_desta_prefeitura` NÃO EXISTEM MAIS.
//    O backend passou a responder `contrato_nao_encontrado` também quando o
//    contrato é de outra pessoa ou de outra organização — deliberadamente
//    indistinguível de inexistente, para não confirmar a existência de registro
//    alheio a quem está sondando ADFs. Eles seguem mapeados aqui por segurança
//    (um deploy antigo pode responder com eles), mas com a mesma frase.
//
// 2. O REASON CRU NÃO VAI MAIS PARA A TELA. `contrato_nao_encontrado` não
//    significa nada para quem está do outro lado, e imprimir token interno é o
//    mesmo modo de falha que este trabalho fecha. A depuração migrou para o
//    `console.warn` do chamador — continua a um F12 de distância.
//
// Mapeia POR REASON primeiro, status só como desempate: o mesmo
// `r2_indisponivel` chega como 503 pela rota do servidor e como 404 pela da
// prefeitura, então guardar por status é frágil — e era o que engolia esse caso.

export type ContextoCcb = "servidor" | "prefeitura";

/** `contrato_nao_encontrado` passou a significar DUAS coisas: "não existe" e
 *  "não é seu". O texto antigo ("pode estar sincronizando, aguarde e tente
 *  novamente") é falso na segunda leitura — esperar nunca resolve — e ainda
 *  convidava à repetição justamente quem estava sondando. */
const NAO_ENCONTRADO: Record<ContextoCcb, string> = {
  servidor:
    "Não encontramos este contrato na sua lista. Atualize a página; se ele continuar aparecendo e o download falhar, avise o suporte.",
  prefeitura:
    "Contrato não encontrado nesta organização. Confira o ADF; se ele consta na sua lista, atualize a página e tente de novo.",
};

const POR_REASON: Record<string, (ctx: ContextoCcb) => string> = {
  ccb_nao_anexado: () =>
    "A instituição ainda não anexou o contrato assinado. Assim que ela enviar, o download fica disponível aqui.",
  contrato_nao_encontrado: (ctx) => NAO_ENCONTRADO[ctx],
  // Deploys anteriores ainda podem responder com estes dois. Mesma frase do
  // "não encontrado": a distinção que eles carregavam é o defeito corrigido.
  contrato_nao_e_seu: (ctx) => NAO_ENCONTRADO[ctx],
  contrato_nao_e_desta_prefeitura: (ctx) => NAO_ENCONTRADO[ctx],
  arquivo_nao_encontrado_no_r2: () =>
    "O contrato foi anexado, mas o arquivo não está mais disponível. Peça à instituição para reenviar.",
  r2_indisponivel: () =>
    "O armazenamento de contratos está indisponível no momento. Tente novamente em alguns instantes.",
  servidor_nao_identificado: () =>
    "Sua sessão não identificou o servidor. Entre novamente e tente de novo.",
};

const GENERICA = "Não foi possível baixar o contrato agora. Tente novamente em alguns instantes.";

/**
 * A frase que o usuário lê quando o download falha.
 *
 * Reason desconhecido cai na genérica — nunca aparece na tela do jeito que veio.
 * Para diagnosticar o desconhecido, o chamador loga `{status, reason}` no
 * console; é onde essa informação serve, e onde ela não vaza.
 */
export function mensagemDeErroCcb(e: unknown, contexto: ContextoCcb): string {
  const err = e as { status?: number; reason?: string } | null;
  const traduz = err?.reason ? POR_REASON[err.reason] : undefined;
  if (traduz) return traduz(contexto);
  if (typeof err?.status === "number" && err.status >= 500) {
    return "O servidor não conseguiu responder agora. Tente novamente em alguns instantes.";
  }
  return GENERICA;
}
